// @flow

import type { State } from './types';
import { getAllProductTypeLabels } from '../productTypeSettings';

type GlobalState = {
  assortmentSettings: State,
};

export const getAssortmentSettings = (state: GlobalState) => {
  const { assortmentSettings } = state;
  if (!assortmentSettings) {
    throw new Error('Missing assortmentSettings store.');
  }
  return assortmentSettings;
};

export const getFilterButtonText = (state: GlobalState) => (
  getAssortmentSettings(state).filter_button
);

export const getActionableButtonText = (state: GlobalState) => (
  getAssortmentSettings(state).actionable_filter_button
);

export const getRemoveFiltersButtonText = (state: GlobalState) => (
  getAssortmentSettings(state).remove_filters_button
);

export const getCloseText = (state: GlobalState) => (
  getAssortmentSettings(state).close_text
);

export const getMake = (state: GlobalState) => (
  getAssortmentSettings(state).make
);

export const getFueltype = (state: GlobalState) => (
  getAssortmentSettings(state).fueltype
);

export const getPrice = (state: GlobalState) => (
  getAssortmentSettings(state).price
);

export const getSize = (state: GlobalState) => (
  getAssortmentSettings(state).size
);

export const getTransmission = (state: GlobalState) => (
  getAssortmentSettings(state).transmission
);

export const getBodywork = (state: GlobalState) => (
  getAssortmentSettings(state).bodywork
);

export const getProductType = (state: GlobalState) => (
  getAssortmentSettings(state).product_type
);

export const getResultTitle = (state: GlobalState) => (
  getAssortmentSettings(state).result_title
);

export const getResultTitlePlural = (state: GlobalState) => (
  getAssortmentSettings(state).result_title_plural
);

export const getSortOptionNameAsc = (state: GlobalState) => (
  getAssortmentSettings(state).sort_option_name_asc
);

export const getSortOptionNameDesc = (state: GlobalState) => (
  getAssortmentSettings(state).sort_option_name_desc
);

export const getSortOptionPriceAsc = (state: GlobalState) => (
  getAssortmentSettings(state).sort_option_price_asc
);

export const getSortOptionPriceDesc = (state: GlobalState) => (
  getAssortmentSettings(state).sort_option_price_desc
);

export const getAssortmentCategoryTitle = (state: GlobalState) => (
  getAssortmentSettings(state).assortment_category_title
);

export const getAssortmentCustomFilterHeading = (state: GlobalState) => (
  getAssortmentSettings(state).custom_filter_heading
);

export const getAssortmentCustomFilterVbmr = (state: GlobalState) => (
  getAssortmentSettings(state).custom_filter_vbmr
);

export const getAssortmentCustomFilterDeliverytime = (state: GlobalState) => (
  getAssortmentSettings(state).custom_filter_deliverytime
);

export const getAssortmentCustomFilterDeliverytimeDuration = (state: GlobalState) => (
  getAssortmentSettings(state).custom_filter_deliverytime_duration
);

type SelectorMap = { [string]: string };

export const getFacetTranslation = (state: GlobalState, facetName: string) => {
  const selectorMap: SelectorMap = {
    bodywork: getBodywork(state),
    make: getMake(state),
    fueltype: getFueltype(state),
    price: getPrice(state),
    size: getSize(state),
    transmission: getTransmission(state),
    product_type: getProductType(state),
  };
  return selectorMap[facetName] || facetName;
};

export const getSortOptionTranslation = (state: GlobalState, sortOption: string) => {
  const selectorMap: SelectorMap = {
    'make:asc': getSortOptionNameAsc(state),
    'make:desc': getSortOptionNameDesc(state),
    'price:asc': getSortOptionPriceAsc(state),
    'price:desc': getSortOptionPriceDesc(state),
  };
  return selectorMap[sortOption] || sortOption;
};

const translatable = {
  size: (state, bucket) => (
    getAssortmentSettings(state)[`size_${bucket}`]
  ),
  product_type: (state, bucket) => (
    getAllProductTypeLabels(state)[bucket] || bucket
  ),
};

export const getBucketTranslation = (state: GlobalState, label: string, bucket: string): string => {
  const fn = translatable[label];
  if (!fn) {
    return bucket;
  }
  return fn(state, bucket) || bucket;
};
